import React, { useEffect, useState } from "react";
import GlobalHeader from "../../../components/GlobalHeader";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FaTelegramPlane } from "react-icons/fa";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";

// Telegram del equipo (Backend/src/routes/admin/telegram.js): estado del bot
// y los números que reciben los avisos del admin. La persona se conecta con
// el enlace de invitación y comparte su número desde el bot; solo entra si el
// número está aquí y activo.

const AZUL = "#229ED9";

const fecha = (valor) =>
  valor
    ? new Date(valor).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })
    : "";

const TelegramAdmin = () => {
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadingApi } = useApi();
  const [datos, setDatos] = useState(null);
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [eligiendo, setEligiendo] = useState(null); // { miembro, marcados: Set }
  const [quitando, setQuitando] = useState(null); // miembro

  const cargar = () =>
    loadApi("admin/telegram", true, "get")
      .then((res) => setDatos(res.data))
      .catch((e) => showSnack(e.message, "error"));

  useEffect(() => {
    cargar();
  }, []);

  const reemplazar = (miembro) =>
    setDatos((d) => ({ ...d, miembros: d.miembros.map((m) => (m.id === miembro.id ? miembro : m)) }));

  const darDeAlta = () =>
    loadApi("admin/telegram", true, "post", { nombre, telefono })
      .then((res) => {
        showSnack(res.data.message);
        setNombre("");
        setTelefono("");
        setDatos((d) => ({ ...d, miembros: [...d.miembros, res.data.miembro] }));
      })
      .catch((e) => showSnack(e.message, "error"));

  // El switch cambia al instante; si no se guardó, vuelve a como estaba.
  const pausar = (miembro, activo) => {
    reemplazar({ ...miembro, activo });
    loadApi(`admin/telegram/${miembro.id}`, true, "put", { activo })
      .then((res) => showSnack(res.data.message))
      .catch((e) => {
        reemplazar(miembro);
        showSnack(e.message, "error");
      });
  };

  const probar = (miembro) =>
    loadApi(`admin/telegram/${miembro.id}/prueba`, true, "post", {})
      .then((res) => showSnack(res.data.message))
      .catch((e) => {
        showSnack(e.message, "error");
        cargar();
      });

  const quitar = () =>
    loadApi(`admin/telegram/${quitando.id}`, true, "delete")
      .then((res) => {
        showSnack(res.data.message);
        setDatos((d) => ({ ...d, miembros: d.miembros.filter((m) => m.id !== quitando.id) }));
        setQuitando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const abrirAvisos = (miembro) =>
    setEligiendo({
      miembro,
      marcados: new Set(miembro.avisos || datos.avisos.map((a) => a.clave)),
    });

  const marcar = (clave, si) =>
    setEligiendo((e) => {
      const marcados = new Set(e.marcados);
      if (si) marcados.add(clave);
      else marcados.delete(clave);
      return { ...e, marcados };
    });

  const guardarAvisos = () =>
    loadApi(`admin/telegram/${eligiendo.miembro.id}`, true, "put", {
      avisos: [...eligiendo.marcados],
    })
      .then((res) => {
        showSnack(res.data.message);
        reemplazar(res.data.miembro);
        setEligiendo(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  // Sin la API del portapapeles (por ejemplo, fuera de https) también avisa.
  const copiar = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(datos.invitacion))
      .then(() => showSnack("Enlace copiado."))
      .catch(() => showSnack("No se pudo copiar: selecciónalo y cópialo a mano.", "warning"));

  const estadoBot = !datos
    ? null
    : !datos.disponible
    ? { label: "Sin configurar", color: "default" }
    : datos.error
    ? { label: "Con error", color: "error" }
    : datos.listo
    ? { label: "Funcionando", color: "success" }
    : { label: "Arrancando", color: "warning" };

  const totalAvisos = datos?.avisos.length || 0;

  return (
    <>
      <GlobalHeader />
      <Container maxWidth="md" sx={{ pb: 6 }}>
        <Stack gap={3} pt={2}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <FaTelegramPlane color={AZUL} fontSize="34px" />
            <Typography variant="h4" color="primary.main" fontWeight="900">
              Telegram
            </Typography>
            {estadoBot && <Chip label={estadoBot.label} color={estadoBot.color} size="small" />}
          </Stack>

          {datos && !datos.disponible && (
            <Alert severity="info">
              Falta configurar el bot. En el servidor, agrega al archivo{" "}
              <strong>.env</strong> del backend <strong>TELEGRAM_BOT_TOKEN</strong> (el que te da
              @BotFather) y <strong>TELEGRAM_WEBHOOK_SECRET</strong>, y reinicia la API (
              <code>pm2 restart API</code>). Mientras tanto ya puedes dar de alta los números.
            </Alert>
          )}
          {datos?.disponible && datos.error && (
            <Alert severity="error">El bot no pudo arrancar: {datos.error}</Alert>
          )}
          {datos?.disponible && !datos.error && (
            <Alert severity="success">
              Bot {datos.bot} funcionando. {datos.usuariosConectados}{" "}
              {datos.usuariosConectados === 1 ? "cuenta conectó" : "cuentas conectaron"} su Telegram
              desde su perfil.
            </Alert>
          )}

          <Typography variant="h5">Avisos del equipo</Typography>
          <Typography variant="body2" color="GrayText">
            Solo los números que des de alta aquí reciben por Telegram los avisos del admin
            (registros, verificaciones, reportes, retiros…). Para conectarse, cada persona abre
            el enlace de invitación y toca «Compartir mi número»: Telegram confirma que el número
            es suyo.
          </Typography>

          {datos?.invitacion && (
            <Stack gap={0.5}>
              <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                <TextField
                  size="small"
                  label="Enlace de invitación"
                  value={datos.invitacion}
                  InputProps={{ readOnly: true }}
                  onFocus={(e) => e.target.select()}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={copiar}>
                  Copiar
                </Button>
              </Stack>
              <Typography variant="caption" color="GrayText">
                Es el mismo para todos y solo sirve con un número dado de alta: puedes mandarlo
                por WhatsApp o por correo.
              </Typography>
            </Stack>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "flex-start" }}>
              <TextField
                size="small"
                label="Nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Teléfono"
                placeholder="+52 55 1234 5678"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                helperText="Con + y la lada del país, como aparece en su Telegram"
                sx={{ flex: 1 }}
              />
              <LoadingButton
                variant="contained"
                loading={loadingApi.includes("post__admin/telegram")}
                disabled={!nombre.trim() || !telefono.trim()}
                onClick={darDeAlta}
              >
                Dar de alta
              </LoadingButton>
            </Stack>
          </Paper>

          {datos && datos.miembros.length === 0 && (
            <Typography variant="body2" color="GrayText">
              Todavía no hay números dados de alta.
            </Typography>
          )}

          {datos && datos.miembros.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Avisos</TableCell>
                    <TableCell>Recibe</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datos.miembros.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{m.nombre}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{m.telefono}</TableCell>
                      <TableCell>
                        {m.conectado ? (
                          <Stack>
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="Conectado"
                              sx={{ alignSelf: "flex-start" }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {[m.telegram, m.conectadoEn && `desde el ${fecha(m.conectadoEn)}`]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Stack>
                        ) : (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Falta que se conecte"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button size="small" variant="outlined" onClick={() => abrirAvisos(m)}>
                          {m.avisos ? `${m.avisos.length} de ${totalAvisos}` : "Todos"}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={m.activo}
                          onChange={(e) => pausar(m, e.target.checked)}
                          color="success"
                          inputProps={{ "aria-label": `Recibe avisos: ${m.nombre}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={1}>
                          <LoadingButton
                            size="small"
                            variant="outlined"
                            disabled={!m.conectado || !datos.disponible}
                            loading={loadingApi.includes(`post__admin/telegram/${m.id}/prueba`)}
                            onClick={() => probar(m)}
                          >
                            Prueba
                          </LoadingButton>
                          <Button size="small" variant="text" color="error" onClick={() => setQuitando(m)}>
                            Quitar
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Container>

      <Dialog open={Boolean(eligiendo)} onClose={() => setEligiendo(null)} fullWidth maxWidth="sm">
        {eligiendo && (
          <>
            <DialogTitle>Avisos para {eligiendo.miembro.nombre}</DialogTitle>
            <DialogContent>
              <Stack>
                {datos.avisos.map((a) => (
                  <FormControlLabel
                    key={a.clave}
                    control={
                      <Checkbox
                        checked={a.siempre || eligiendo.marcados.has(a.clave)}
                        disabled={a.siempre}
                        onChange={(e) => marcar(a.clave, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {a.codigo} · {a.nombre}
                        {a.siempre && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {" "}
                            (siempre: es de seguridad)
                          </Typography>
                        )}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                sx={{ mr: "auto" }}
                onClick={() =>
                  setEligiendo((e) => ({ ...e, marcados: new Set(datos.avisos.map((a) => a.clave)) }))
                }
              >
                Marcar todos
              </Button>
              <Button onClick={() => setEligiendo(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                loading={loadingApi.includes(`put__admin/telegram/${eligiendo.miembro.id}`)}
                onClick={guardarAvisos}
              >
                Guardar
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={Boolean(quitando)} onClose={() => setQuitando(null)}>
        {quitando && (
          <>
            <DialogTitle>¿Quitar a {quitando.nombre}?</DialogTitle>
            <DialogContentText sx={{ px: 3 }}>
              Dejará de recibir los avisos del equipo por Telegram. Si después lo vuelves a dar de
              alta, tendrá que abrir otra vez el enlace de invitación.
            </DialogContentText>
            <DialogActions>
              <Button onClick={() => setQuitando(null)}>Cancelar</Button>
              <LoadingButton
                color="error"
                variant="contained"
                loading={loadingApi.includes(`delete__admin/telegram/${quitando.id}`)}
                onClick={quitar}
              >
                Quitar
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
};

export default TelegramAdmin;
